import { useEffect, useState } from 'react'
import { Check, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { api } from '@/lib/api'
import { useAuthStore } from '@/stores/auth-store'

// Kept in this frontend module (no shared types package). Mirrors the backend
// NotificationType union + EMAIL_DEFAULTS in backend/src/types/notifications.ts.
export type NotificationType =
  | 'section_assigned'
  | 'document_owner_assigned'
  | 'review_requested'
  | 'changes_requested'
  | 'review_approved'
  | 'comment_added'
  | 'comment_reply'
  | 'comment_resolved'
  | 'mention'
  | 'member_added'
  | 'ownership_transferred'
  | 'key_date_changed'
  | 'analysis_ready'
  | 'feedback_resolved'
  | 'draft_ready'

export interface NotificationPrefs {
  emailEnabled?: boolean
  types?: Partial<Record<NotificationType, boolean>>
}

// Shipped default per type. Every type on except review_approved (loop-closing FYI).
const EMAIL_DEFAULTS: Record<NotificationType, boolean> = {
  section_assigned: true,
  document_owner_assigned: true,
  review_requested: true,
  changes_requested: true,
  review_approved: false,
  comment_added: true,
  comment_reply: true,
  comment_resolved: true,
  mention: true,
  member_added: true,
  ownership_transferred: true,
  key_date_changed: true,
  analysis_ready: true,
  feedback_resolved: true,
  draft_ready: true,
}

interface TypeConfig {
  type: NotificationType
  label: string
}

interface TypeGroup {
  heading: string
  types: TypeConfig[]
}

const GROUPS: TypeGroup[] = [
  {
    heading: 'Assignments',
    types: [
      { type: 'section_assigned', label: 'Section assigned to me' },
      { type: 'document_owner_assigned', label: 'Made owner of a response' },
    ],
  },
  {
    // Long-running AI work you kicked off yourself. It's the only group whose
    // notification exists so you can safely leave the page, so it gets its own
    // heading rather than being filed under someone else's activity.
    // Placeholder heading and label (R1 P3′) until Kapil's hand test.
    heading: 'AI work you started',
    types: [
      // "results", not "finished": this type also carries the FAILURE notice,
      // so a label promising only success would let someone switch off the
      // "your analysis failed, retry it" email without knowing they had.
      { type: 'analysis_ready', label: 'Analysis results (compliance & submission)' },
      // Also sent when a document job stops before it finishes, so the label
      // names both (same reason as "results" above).
      { type: 'draft_ready', label: 'Document drafting finished or stopped' },
    ],
  },
  {
    heading: 'Reviews',
    types: [
      { type: 'review_requested', label: 'Review requested or cancelled' },
      { type: 'changes_requested', label: 'Changes requested on my section' },
      { type: 'review_approved', label: 'My section approved' },
      { type: 'key_date_changed', label: 'Key date changes (addenda)' },
    ],
  },
  {
    heading: 'Comments',
    types: [
      { type: 'comment_added', label: 'New comment on my work' },
      { type: 'comment_reply', label: 'Reply in my thread' },
      { type: 'comment_resolved', label: 'My comment is resolved or reopened' },
      { type: 'mention', label: "I'm @mentioned" },
    ],
  },
  {
    heading: 'Membership',
    types: [
      { type: 'member_added', label: 'Added to a bid' },
      { type: 'ownership_transferred', label: 'Bid ownership changes' },
      { type: 'feedback_resolved', label: 'Fixes to issues I reported' },
    ],
  },
]

export function NotificationPreferences() {
  const userId = useAuthStore((state) => state.user?.id ?? null)

  const [emailEnabled, setEmailEnabled] = useState(true)
  const [types, setTypes] = useState<Record<NotificationType, boolean>>({ ...EMAIL_DEFAULTS })
  // Saved snapshots for dirty detection (mirrors the profile form pattern).
  const [savedEmailEnabled, setSavedEmailEnabled] = useState(true)
  const [savedTypes, setSavedTypes] = useState<Record<NotificationType, boolean>>({ ...EMAIL_DEFAULTS })

  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Load prefs from GET /users/me — this panel is the source of truth for
  // notification preferences. Missing keys fall back to defaults.
  useEffect(() => {
    let active = true
    void (async () => {
      try {
        const { user } = await api.get<{ user: { notificationPrefs?: NotificationPrefs } }>('/users/me')
        if (!active) return
        const prefs = user?.notificationPrefs ?? {}
        const nextEmail = prefs.emailEnabled ?? true
        const nextTypes = { ...EMAIL_DEFAULTS, ...(prefs.types ?? {}) }
        setEmailEnabled(nextEmail)
        setTypes(nextTypes)
        setSavedEmailEnabled(nextEmail)
        setSavedTypes(nextTypes)
      } catch {
        if (active) setError('Failed to load notification preferences')
      } finally {
        if (active) setIsLoading(false)
      }
    })()
    return () => {
      active = false
    }
  }, [])

  const isDirty =
    emailEnabled !== savedEmailEnabled ||
    JSON.stringify(types) !== JSON.stringify(savedTypes)

  const handleToggleType = (type: NotificationType, checked: boolean) => {
    setTypes((prev) => ({ ...prev, [type]: checked }))
  }

  const handleSave = async () => {
    if (!userId) return
    setIsSaving(true)
    setError(null)
    try {
      // FULL-REPLACE contract: send the COMPLETE prefs object every save —
      // master switch + an explicit boolean for every type — so the backend
      // never wipes a setting we didn't include.
      const prefs: NotificationPrefs = { emailEnabled, types: { ...types } }
      await api.patch(`/users/${userId}`, { _notificationPrefs: prefs })
      setSavedEmailEnabled(emailEnabled)
      setSavedTypes({ ...types })
      setSaved(true)
      setTimeout(() => setSaved(false), 2000)
    } catch {
      setError('Failed to save notification preferences')
    } finally {
      setIsSaving(false)
    }
  }

  if (isLoading) {
    return (
      <section className="space-y-4">
        <h2 className="text-sm font-medium">Notification preferences</h2>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading preferences...
        </div>
      </section>
    )
  }

  return (
    <section className="space-y-6">
      <div className="space-y-1">
        <h2 className="text-sm font-medium">Notification preferences</h2>
        <p className="text-meta">
          Choose which activity sends you an email. In-app notifications are always shown.
        </p>
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      {/* Master email switch */}
      <div className="flex items-center justify-between gap-4">
        <div className="space-y-0.5">
          <Label htmlFor="email-notifications" className="text-sm cursor-pointer">
            Email notifications
          </Label>
          <p className="text-meta">
            Master switch — turn off to stop all notification emails.
          </p>
        </div>
        <Switch
          id="email-notifications"
          checked={emailEnabled}
          onCheckedChange={setEmailEnabled}
        />
      </div>

      {/* Per-type groups */}
      {GROUPS.map((group) => (
        <div key={group.heading} className="space-y-3">
          <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            {group.heading}
          </h3>
          <div className="space-y-3">
            {group.types.map(({ type, label }) => (
              <div key={type} className="flex items-center justify-between gap-4">
                <Label
                  htmlFor={`notif-${type}`}
                  className="text-sm font-normal cursor-pointer"
                >
                  {label}
                </Label>
                <Switch
                  id={`notif-${type}`}
                  checked={types[type]}
                  disabled={!emailEnabled}
                  onCheckedChange={(checked) => handleToggleType(type, checked)}
                />
              </div>
            ))}
          </div>
        </div>
      ))}

      <div className="flex justify-end mt-6">
        <Button onClick={() => void handleSave()} disabled={isSaving || !isDirty}>
          {isSaving ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              Saving...
            </>
          ) : saved ? (
            <>
              <Check className="h-4 w-4 mr-2" />
              Saved
            </>
          ) : (
            'Save Preferences'
          )}
        </Button>
      </div>
    </section>
  )
}
